import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { toast } from 'sonner';
import { Building2, CheckCircle, Percent, Handshake, Loader2, ShieldCheck } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Page publique — « Devenez partenaire » (Lot 6). Vérification SIRET certifiée.
export default function DevenirPartenairePage() {
  const [config, setConfig] = useState({ metiers: ['DJ', 'Éclairagiste', 'Traiteur', 'Photographe', 'Autre'], commission_taux: 15 });
  const [siret, setSiret] = useState('');
  const [verif, setVerif] = useState(null);      // {raison_sociale, adresse}
  const [verifying, setVerifying] = useState(false);
  const [form, setForm] = useState({ metier: '', nom: '', prenom: '', email: '', telephone: '', iban: '', bic: '', titulaire: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/apporteurs/config`).then((r) => r.json()).then((d) => {
      if (d && d.page_publique_active === false) setDone(false);
      if (d?.metiers) setConfig(d);
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function verifySiret() {
    const s = siret.replace(/\D/g, '');
    if (s.length !== 9 && s.length !== 14) { toast.error('SIREN (9) ou SIRET (14) chiffres attendu.'); return; }
    setVerifying(true); setVerif(null);
    try {
      const r = await fetch(`${BACKEND}/api/apporteurs/siret/${s}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Introuvable');
      setVerif(d);
      if (!form.titulaire) setForm((f) => ({ ...f, titulaire: d.raison_sociale }));
      toast.success('Entreprise vérifiée.');
    } catch (e) {
      toast.error(e.message || 'Vérification impossible.');
    } finally {
      setVerifying(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!verif) { toast.error('Vérifiez d\'abord votre SIRET.'); return; }
    if (!form.email || !form.telephone || !form.metier) { toast.error('Métier, email et téléphone obligatoires.'); return; }
    setSubmitting(true);
    try {
      const body = {
        siret: siret.replace(/\D/g, ''), raison_sociale: verif.raison_sociale, adresse: verif.adresse,
        metier: form.metier, nom: form.nom, prenom: form.prenom, email: form.email, telephone: form.telephone,
        rib: { iban: form.iban, bic: form.bic, titulaire: form.titulaire },
      };
      const r = await fetch(`${BACKEND}/api/apporteurs/candidature`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erreur');
      setDone(true);
    } catch (err) {
      toast.error(err.message || 'Envoi impossible.');
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--ink)', fontSize: 14 };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '48px 20px 80px' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, background: 'var(--teal-mist)', color: 'var(--teal)', fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
            <Handshake size={15} /> Programme partenaires
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 800, color: 'var(--ink)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>Devenez partenaire</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 17, maxWidth: 620, margin: '14px auto 0', lineHeight: 1.6 }}>
            DJ, éclairagiste, traiteur, photographe : recommandez notre matériel à vos clients et touchez
            <strong style={{ color: 'var(--ink)' }}> {config.commission_taux}% de commission</strong> sur chaque dossier réalisé.
          </p>
        </div>

        {/* Avantages */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16, marginBottom: 40 }}>
          {[
            { icon: <Percent size={20} />, t: `${config.commission_taux}% de commission`, d: 'Sur le prix final payé par le client, à la clôture du dossier.' },
            { icon: <ShieldCheck size={20} />, t: 'Validation par SIRET', d: 'Inscription vérifiée sur les données officielles des entreprises.' },
            { icon: <Building2 size={20} />, t: 'Deux façons de commander', d: 'Pour vous, ou directement pour votre client au tarif public.' },
          ].map((a, i) => (
            <div key={i} style={{ background: 'var(--surface,#fff)', border: '1px solid var(--border)', borderRadius: 16, padding: 20 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--teal-mist)', color: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>{a.icon}</div>
              <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{a.t}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{a.d}</div>
            </div>
          ))}
        </div>

        {/* Formulaire */}
        {done ? (
          <div data-testid="candidature-success" style={{ textAlign: 'center', background: 'var(--surface,#fff)', border: '1px solid var(--border)', borderRadius: 20, padding: 40 }}>
            <CheckCircle size={54} style={{ color: 'var(--teal)', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--ink)' }}>Candidature reçue !</h2>
            <p style={{ color: 'var(--text-muted)', marginTop: 10, maxWidth: 460, marginInline: 'auto', lineHeight: 1.6 }}>
              L'exploitant va vérifier votre entreprise et vous répondra par e-mail. Une fois validé, vous recevrez un lien pour activer votre espace partenaire.
            </p>
            <Link to="/" style={{ display: 'inline-block', marginTop: 22, padding: '12px 24px', borderRadius: 12, background: 'var(--teal)', color: '#fff', fontWeight: 700, textDecoration: 'none' }}>Retour à l'accueil</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="candidature-form" style={{ background: 'var(--surface,#fff)', border: '1px solid var(--border)', borderRadius: 20, padding: 28 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink)', marginBottom: 18 }}>Votre candidature</h2>

            {/* SIRET */}
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>SIRET de votre entreprise</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input data-testid="input-siret" value={siret} onChange={(e) => setSiret(e.target.value)} placeholder="123 456 789 00012" style={inputStyle} />
              <button type="button" data-testid="btn-verifier-siret" onClick={verifySiret} disabled={verifying}
                style={{ padding: '0 18px', borderRadius: 12, background: 'var(--ink)', color: '#fff', fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {verifying ? <Loader2 size={16} className="animate-spin" /> : 'Vérifier'}
              </button>
            </div>
            {verif && (
              <div data-testid="siret-verifie" style={{ marginTop: 10, padding: 12, borderRadius: 12, background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.3)' }}>
                <div style={{ fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle size={15} style={{ color: 'var(--teal)' }} /> {verif.raison_sociale}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{verif.adresse}</div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
              <Field label="Métier">
                <select data-testid="input-metier" value={form.metier} onChange={set('metier')} style={inputStyle}>
                  <option value="">Choisir…</option>
                  {config.metiers.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Téléphone"><input data-testid="input-telephone" value={form.telephone} onChange={set('telephone')} style={inputStyle} /></Field>
              <Field label="Prénom"><input data-testid="input-prenom" value={form.prenom} onChange={set('prenom')} style={inputStyle} /></Field>
              <Field label="Nom"><input data-testid="input-nom" value={form.nom} onChange={set('nom')} style={inputStyle} /></Field>
              <Field label="Email" span><input data-testid="input-email" type="email" value={form.email} onChange={set('email')} style={inputStyle} /></Field>
            </div>

            <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', margin: '22px 0 10px' }}>Coordonnées bancaires (pour vos commissions)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
              <Field label="IBAN"><input data-testid="input-iban" value={form.iban} onChange={set('iban')} placeholder="FR76 …" style={inputStyle} /></Field>
              <Field label="BIC"><input data-testid="input-bic" value={form.bic} onChange={set('bic')} style={inputStyle} /></Field>
              <Field label="Titulaire du compte" span><input data-testid="input-titulaire" value={form.titulaire} onChange={set('titulaire')} style={inputStyle} /></Field>
            </div>

            <button type="submit" data-testid="btn-envoyer-candidature" disabled={submitting}
              style={{ width: '100%', marginTop: 24, padding: '15px', borderRadius: 12, background: 'var(--teal)', color: '#fff', fontWeight: 800, fontSize: 15, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Envoyer ma candidature'}
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}

const Field = ({ label, span, children }) => (
  <div style={{ gridColumn: span ? '1 / -1' : 'auto' }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);
